import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TextField,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Container,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import { UserContext } from "../../context/UserContext";

const SYM_HISTORY_URL = `${process.env.REACT_APP_API_URL}/api/admin/history/symHistory`;

const System = () => {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [symHistory, setSymHistory] = useState([]);
  const [filteredSymHistory, setFilteredSymHistory] = useState([]);
  const [symLoading, setSymLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState("today");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Store latest branchName for each branchUsername
  const [branchNameMap, setBranchNameMap] = useState({});

  const yellowButton = {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  };

  // Date range options - Only up to monthly
  const dateRangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "custom", label: "Custom Range" },
  ];

  // Fetch Symmetric History
  const fetchSymHistory = useCallback(async () => {
    if (!user?.company) return;
    try {
      setSymLoading(true);
      const token = user.token;
      const res = await axios.get(SYM_HISTORY_URL, {
        headers: { Authorization: `Bearer ${token}` },
        params: { company: user.company },
      });

      if (res.data.status === "success") {
        const historyData = res.data.data;
        setSymHistory(historyData);
        
        const nameMap = {};
        historyData.forEach(item => {
          if (item.branchUsername && item.branchName) {
            nameMap[item.branchUsername] = item.branchName;
          }
        });
        setBranchNameMap(nameMap);
      } else {
        console.error("Failed to fetch Symmetric history:", res.data.message);
      }
    } catch (err) {
      console.error("Symmetric history fetch error:", err);
    } finally {
      setSymLoading(false);
    }
  }, [user]);

  // Refresh all data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSymHistory();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchSymHistory();
  }, [fetchSymHistory]);

  // Apply filters to symHistory
  useEffect(() => {
    let filtered = [...symHistory];

    // Apply date range filter
    filtered = filtered.filter(item => {
      const timestampField = item.time || item.createdAt;
      const itemDate = new Date(timestampField);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (showCustomDate && fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return itemDate >= from && itemDate <= to;
      }

      switch (dateRange) {
        case "today":
          return itemDate >= today;
        case "yesterday": {
          const yesterday = new Date(now);
          yesterday.setDate(now.getDate() - 1);
          yesterday.setHours(0, 0, 0, 0);
          const yesterdayEnd = new Date(now);
          yesterdayEnd.setDate(now.getDate() - 1);
          yesterdayEnd.setHours(23, 59, 59, 999);
          return itemDate >= yesterday && itemDate <= yesterdayEnd;
        }
        case "thisWeek": {
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          return itemDate >= startOfWeek;
        }
        case "thisMonth": {
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          return itemDate >= startOfMonth;
        }
        case "lastMonth": {
          const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
          endOfLastMonth.setHours(23, 59, 59, 999);
          return itemDate >= startOfLastMonth && itemDate <= endOfLastMonth;
        }
        default:
          return true;
      }
    });

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.state === statusFilter);
    }

    setFilteredSymHistory(filtered);
  }, [symHistory, dateRange, statusFilter, fromDate, toDate, showCustomDate]);

  const handleDateRangeChange = (event) => {
    const newValue = event.target.value;
    setDateRange(newValue);
    setShowCustomDate(newValue === "custom");
    if (newValue !== "custom") {
      setFromDate(null);
      setToDate(null);
    }
  };

  const handleResetFilters = () => {
    setStatusFilter("all");
    setDateRange("today");
    setFromDate(null);
    setToDate(null);
    setShowCustomDate(false);
  };

  // Get all unique branch usernames and sort them
  const uniqueBranchUsernames = [...new Set(symHistory.map(item => item.branchUsername))].filter(Boolean).sort();

  // Group filtered symHistory by timestamp
  const groupedByTimestamp = filteredSymHistory.reduce((acc, item) => {
    const timestampField = item.time || item.createdAt;
    const date = new Date(timestampField);
    const formattedTimestamp = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    if (!acc[formattedTimestamp]) {
      acc[formattedTimestamp] = {
        data: {},
        details: {}
      };
    }

    acc[formattedTimestamp].data[item.branchUsername] = item.responseTime;
    acc[formattedTimestamp].details[item.branchUsername] = {
      state: item.state,
      benId: item.benId,
      branchName: item.branchName,
      branchUsername: item.branchUsername,
      responseTime: item.responseTime,
      avgResponseTime: item.avgResponseTime
    };

    return acc;
  }, {});

  // Convert to array for table display
  let symTableData = Object.entries(groupedByTimestamp).map(([timestamp, value]) => ({
    sno: 0,
    timestamp,
    ...value.data,
    _details: value.details
  }));

  // Sort by timestamp (newest first)
  symTableData.sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return dateB - dateA;
  });

  // Add serial numbers after sorting
  symTableData = symTableData.map((row, index) => ({
    ...row,
    sno: index + 1
  }));

  // Get display name for a branch
  const getBranchDisplayName = (branchUsername) => {
    const latestBranchName = branchNameMap[branchUsername] || branchUsername;
    return isMobile ? branchUsername : `${branchUsername} (${latestBranchName})`;
  };

  // Function to get tooltip details
  const getTooltipDetails = (branchUsername, timestamp) => {
    const row = symTableData.find(r => r.timestamp === timestamp);
    if (row && row._details && row._details[branchUsername]) {
      const details = row._details[branchUsername];
      return `Ben ID: ${details.benId}\nBranch: ${details.branchName}\nUsername: ${details.branchUsername}\nState: ${details.state}\nResponse: ${details.responseTime}ms\nAvg Response: ${details.avgResponseTime || 'N/A'}ms`;
    }
    return "No data available";
  };

  if (symLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: { xs: 1, sm: 2 } }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1, sm: 2, md: 0.5 },
          boxSizing: "border-box",
        }}
      >
        {/* ===== SYMMETRIC HISTORY TABLE ===== */}
        <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2 }, overflowX: "auto" }}>
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              mb: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              bgcolor: "#fafafa",
            }}
          >
            {/* Filters - Responsive */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ 
                  color: "#000",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  mb: { xs: 1, sm: 0 }
                }}
              >
                System History
              </Typography>

              <FormControl
                size="small"
                fullWidth={isMobile}
                sx={{
                  minWidth: { xs: "100%", sm: 150 },
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 36, sm: 40 },
                  },
                }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active Only</MenuItem>
                  <MenuItem value="Down">Down Only</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                fullWidth={isMobile}
                sx={{
                  minWidth: { xs: "100%", sm: 150 },
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 36, sm: 40 },
                  },
                }}
              >
                <InputLabel>Date</InputLabel>
                <Select
                  value={dateRange}
                  label="Date"
                  onChange={handleDateRangeChange}
                >
                  {dateRangeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showCustomDate && (
                <>
                  <TextField
                    type="date"
                    size="small"
                    label="From"
                    value={fromDate || ""}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth={isMobile}
                    sx={{
                      minWidth: { xs: "100%", sm: 150 },
                      "& .MuiOutlinedInput-root": {
                        height: { xs: 36, sm: 40 },
                      },
                    }}
                  />

                  <TextField
                    type="date"
                    size="small"
                    label="To"
                    value={toDate || ""}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth={isMobile}
                    sx={{
                      minWidth: { xs: "100%", sm: 150 },
                      "& .MuiOutlinedInput-root": {
                        height: { xs: 36, sm: 40 },
                      },
                    }}
                  />
                </>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "space-between", sm: "flex-start" },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...yellowButton,
                    background: "transparent",
                    border: "1px solid #FFD600",
                    flex: { xs: 1, sm: 0 },
                  }}
                >
                  Reset
                </Button>

                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...yellowButton,
                    background: "transparent",
                    border: "1px solid #FFD600",
                  }}
                >
                  {refreshing ? (
                    <CircularProgress size={isMobile ? 16 : 20} />
                  ) : (
                    <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Filter Summary Chips - Responsive */}
            {(statusFilter !== "all" || dateRange !== "today" || (fromDate && toDate)) && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1.5 }}>
                {statusFilter !== "all" && (
                  <Chip
                    label={`Status: ${statusFilter}`}
                    size="small"
                    onDelete={() => setStatusFilter("all")}
                  />
                )}
                {dateRange !== "today" && !showCustomDate && (
                  <Chip
                    label={`Date: ${dateRangeOptions.find(opt => opt.value === dateRange)?.label}`}
                    size="small"
                    onDelete={() => setDateRange("today")}
                  />
                )}
                {showCustomDate && fromDate && toDate && (
                  <Chip
                    label={`${fromDate} to ${toDate}`}
                    size="small"
                    onDelete={() => {
                      setShowCustomDate(false);
                      setDateRange("today");
                      setFromDate(null);
                      setToDate(null);
                    }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Records Count */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
            Showing {symTableData.length} record{symTableData.length !== 1 ? 's' : ''}
          </Typography>

          {symTableData.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 4, color: "#666666" }}>
              No system history data available for the selected filters
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: "500px", overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        bgcolor: "#f5f5f5",
                        minWidth: 50,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 }
                      }}
                    >
                      RNO
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        bgcolor: "#f5f5f5",
                        minWidth: { xs: 140, sm: 200 },
                        position: "sticky",
                        left: { xs: 40, sm: 60 },
                        zIndex: 2,
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 }
                      }}
                    >
                      Date & Time
                    </TableCell>
                    {uniqueBranchUsernames.map((branchUsername) => (
                      <TableCell
                        key={branchUsername}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "#f5f5f5",
                          minWidth: { xs: 100, sm: 180 },
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 }
                        }}
                      >
                        <Tooltip title={`Username: ${branchUsername}`}>
                          <span>{isMobile ? branchUsername.substring(0, 8) : getBranchDisplayName(branchUsername)}</span>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {symTableData.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: 0,
                          bgcolor: "white",
                          zIndex: 1,
                          fontWeight: "medium",
                          textAlign: "center",
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 }
                        }}
                      >
                        {row.sno}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: { xs: 40, sm: 60 },
                          bgcolor: "white",
                          zIndex: 1,
                          fontWeight: "medium",
                          fontSize: { xs: "0.6rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 }
                        }}
                      >
                        {row.timestamp}
                      </TableCell>
                      {uniqueBranchUsernames.map((branchUsername) => {
                        const responseTime = row[branchUsername];
                        const branchDetails = row._details?.[branchUsername];
                        const state = branchDetails?.state;
                        const avgResponseTime = branchDetails?.avgResponseTime;

                        let color = "#00e676";
                        let bgColor = "transparent";
                        let borderLeft = "none";

                        if (responseTime) {
                          if (responseTime > 300) {
                            color = "#ff4444";
                            bgColor = "#ffebee";
                          } else if (responseTime > 100) {
                            color = "#ff9800";
                            bgColor = "#fff3e0";
                          } else {
                            color = "#00e676";
                            bgColor = "#e8f5e9";
                          }
                        }

                        if (statusFilter !== "all") {
                          if (statusFilter === "Active" && state === "Active") {
                            bgColor = "#c8e6c9";
                            borderLeft = "3px solid #4caf50";
                          } else if (statusFilter === "Down" && state === "Down") {
                            bgColor = "#ffcdd2";
                            borderLeft = "3px solid #f44336";
                          }
                        } else {
                          if (state === "Down") {
                            borderLeft = "3px solid #f44336";
                            bgColor = "#ffebee";
                          } else if (state === "Active") {
                            borderLeft = "3px solid #4caf50";
                          }
                        }

                        return (
                          <TableCell
                            key={branchUsername}
                            align="center"
                            sx={{
                              color: color,
                              fontWeight: responseTime && responseTime > 100 ? "bold" : "normal",
                              bgcolor: bgColor,
                              borderLeft: borderLeft,
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              position: "relative",
                              py: { xs: 0.5, sm: 1.5 },
                              fontSize: { xs: "0.6rem", sm: "0.875rem" },
                              p: { xs: 0.5, sm: 1 }
                            }}
                            title={getTooltipDetails(branchUsername, row.timestamp)}
                          >
                            <Box sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "center", 
                              gap: { xs: 0.5, sm: 1 }, 
                              flexWrap: "wrap",
                              flexDirection: { xs: "column", sm: "row" }
                            }}>
                              <Typography component="span" sx={{ fontWeight: "inherit", fontSize: "inherit" }}>
                                {responseTime ? `${responseTime}ms` : "—"}
                              </Typography>

                              {state && (
                                <Chip
                                  label={state}
                                  size="small"
                                  sx={{
                                    height: { xs: 16, sm: 20 },
                                    fontSize: { xs: "0.5rem", sm: "0.7rem" },
                                    bgcolor: state === "Active" ? "#4caf50" : "#f44336",
                                    color: "white"
                                  }}
                                />
                              )}

                              {avgResponseTime && !isMobile && (
                                <Chip
                                  label={`Avg: ${avgResponseTime}ms`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: { xs: 16, sm: 20 },
                                    fontSize: { xs: "0.5rem", sm: "0.65rem" },
                                    color: "#666",
                                    borderColor: "#ccc"
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default System;